<script setup>
import { useOrderStore } from '~/store/order'
import { useFutureStore } from '~/store/future'
import { objectOmit, whenever } from '@vueuse/core'
import { useStore } from '~/store'

const props = defineProps({
  selects: {
    type: Array,
    default: () => [],
  },
  isPageOrder: {
    type: Boolean,
    default: false,
  },
  showConditionTypeN: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['search'])
const { t } = useI18n()
const order = useOrderStore()
const store = useStore()
const future = useFutureStore()

const getN = computed(() => k => future.openOrders.filter(i => i[k]).length)

const baseList = computed(() => store.currencies.map(i => ({
  label: i.name,
  key: i.id,
})))
const codeList = computed(() => store.contracts.map(i => ({
  label: i.code,
  key: i.id,
})))
const tabs = computed(() => {
  return [
    { label: `${t('限价')} | ${t('市价')}${props.showConditionTypeN ? ` (${unref(getN)('lm')})` : ''}`, key: 0 },
    { label: `${t('计划委托')}${props.showConditionTypeN ? ` (${unref(getN)('trigger')})` : ''}`, key: 1 },
    { label: `${t('止盈止损')}${props.showConditionTypeN ? ` (${unref(getN)('tpsl')})` : ''}`, key: 2 },
  ]
})
const dateList = computed(() => {
  return [
    { label: t('1天内'), key: '1' },
    { label: t('1周内'), key: '7' },
    { label: t('1月内'), key: '30' },
  ]
})
const sideList = computed(() => [
  { label: t('买入/做多'), key: 1 },
  { label: t('卖出/做空'), key: -1 },
])
const conditionList = computed(() => [
  { label: t('限价'), key: 1 },
  { label: t('市价'), key: 3 },
  { label: t('限价条件'), key: 2 },
  { label: t('市价条件'), key: 4 },
])
const accountTypeList = computed(() => [
  { label: t('合约主账户'), key: 1 },
  // { label: t('体验金账户'), key: 10 },
])
function search() {
  const params = props.selects.reduce((acc, next) => {
    if (order.params[next] || order.params[next] === 0) {
      acc[next] = order.params[next]
    }
    return acc
  }, {})
  if (order.params.dateSelect) {
    const now = Date.now()
    params.startTime = (now - 86400000 * +order.params.dateSelect) * 1000
    params.endTime = now * 1000
  }
  if (order.params.dateRange) {
    params.startTime = order.params.dateRange[0]
    params.endTime = order.params.dateRange[1]
  }
  emits('search', objectOmit(params, ['dateSelect', 'dateRange']))
}
watch([() => order.current, () => future.id], () => {
  order.params.contractId = order.current ? future.id : undefined
})
watch(() => order.params, search, { deep: true })
</script>

<template>
  <div>
    <CommonXTabs v-if="selects.includes('conditionType') && isPageOrder" v-model="order.conditionType" :tabs="tabs" class="h-6 mb-4 text-xs" />
    <div class="flex items-center justify-between space-x-4">
      <div class="flex items-center space-x-4">
        <CommonXTabs v-if="selects.includes('conditionType') && !isPageOrder" v-model="order.conditionType" :tabs="tabs" class="h-6 text-xs" />
        <CommonTSearchSelect v-if="selects.includes('contractId')" v-model="order.params.contractId" class="w-[164px]" :label="isPageOrder ? $t('合约') : ''" hide-search :wrap-class="{ '!h-8': !isPageOrder }" :all-text="$t('全部合约')" :list="codeList" />
        <CommonTSearchSelect v-if="selects.includes('dateSelect')" v-model="order.params.dateSelect" class="w-[164px]" :label="isPageOrder ? $t('时间') : ''" hide-search :wrap-class="{ '!h-8': !isPageOrder }" :all-text="$t('全部')" :list="dateList" />
        <CommonTSearchSelect v-if="selects.includes('side')" v-model="order.params.side" hide-search class="w-[164px]" :all-text="$t('全部')" :label="isPageOrder ? $t('方向') : ''" :wrap-class="{ '!h-8': !isPageOrder }" :list="sideList" />
        <CommonTSearchSelect v-if="selects.includes('orderCondition')" v-model="order.params.orderCondition" hide-search class="w-[164px]" :all-text="$t('全部类型')" :label="isPageOrder ? $t('订单类型') : ''" :wrap-class="{ '!h-8': !isPageOrder }" :list="conditionList" />
        <CommonTSearchSelect v-if="selects.includes('currencyId')" v-model="order.params.currencyId" :all-text="$t('全部币种')" class="w-[164px]" :list="baseList" :label="isPageOrder ? $t('币种') : ''" />
        <CommonTSearchSelect v-if="selects.includes('type')" v-model="order.params.type" hide-search :all-text="$t('全部类型')" class="w-[164px]" :list="future.accountAssetTypeList" :label="$t('资金记录类型')" />
        <CommonTSearchSelect v-if="selects.includes('accountType')" v-model="order.params.accountType" hide-search :all-text="$t('全部类型')" class="w-[164px]" :list="accountTypeList" :label="$t('账户类型')" />
        <div v-if="selects.includes('dateRage')">
          <div class="mb-2 text-xs text-textTertiary">
            {{ $t('日期') }}
          </div>
          <n-date-picker v-model:value="order.params.dateRange" :start-placeholder="$t('开始时间')" :end-placeholder="$t('结束时间')" size="small" type="daterange" clearable>
            <template #clear>
              &nbsp;
            </template>
            <template #confirm="{ onConfirm }">
              <div class="pb-2">
                <CommonXButton class="!h-8" @click="onConfirm">
                  {{ $t('确认') }}
                </CommonXButton>
              </div>
            </template>
          </n-date-picker>
        </div>
      </div>
      <div class="flex items-center space-x-4">
        <CommonXButton v-if="selects.includes('closeAll')" type="tertiary" size="tiny" class="!px-4" @click="future.modal.flashAll = true">
          {{ $t('一键全平') }}
        </CommonXButton>
        <CommonXButton v-if="selects.includes('cancelAll')" type="tertiary" size="tiny" class="!px-4" @click="future.modal.cancelAll = true">
          {{ $t('一键撤单') }}
        </CommonXButton>
      </div>
    </div>
  </div>
</template>
